import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, MapPin, Clock, ExternalLink } from 'lucide-react';
import L from 'leaflet';
const GOOGLE_MAPS_URL = 'https://www.google.com/maps/dir/?api=1&destination=3840+W+Humphrey+St,+Tampa,+FL+33614';
const LAT = 27.9616;
const LNG = -82.5065;
export function UrgencyCTA() {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      scrollWheelZoom: false,
      dragging: true,
      zoomControl: true
    }).setView([LAT, LNG], 15);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors'
    }).addTo(map);
    const goldIcon = L.divIcon({
      className: '',
      html: `<div style="display:flex;align-items:center;justify-content:center;width:40px;height:40px;background:#C9A84C;border-radius:50%;border:3px solid #1B2A4A;box-shadow:0 2px 8px rgba(0,0,0,0.3);">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1B2A4A" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
      </div>`,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40]
    });
    const marker = L.marker([LAT, LNG], {
      icon: goldIcon
    }).addTo(map);
    marker.bindPopup(`<div style="text-align:center;font-family:Inter,sans-serif;padding:4px;">
        <strong style="font-size:14px;color:#1B2A4A;">Angel Insurance</strong><br/>
        <span style="font-size:12px;color:#555;">3840 W Humphrey St<br/>Tampa, FL 33614</span><br/>
        <a href="${GOOGLE_MAPS_URL}" target="_blank" rel="noopener noreferrer" style="display:inline-block;margin-top:8px;padding:6px 14px;background:#C9A84C;color:#1B2A4A;border-radius:20px;font-size:12px;font-weight:bold;text-decoration:none;">
          Abrir en Google Maps
        </a>
      </div>`);
    mapInstanceRef.current = map;
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);
  return <section className="py-14 md:py-20 bg-off-white" data-id="element-288">
      <div className="container mx-auto px-4" data-id="element-289">
        <div className="max-w-4xl mx-auto" data-id="element-290">
          {/* Urgency Copy */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-10 space-y-6" data-id="element-291">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-8" data-id="element-292">
              NO ESPERES UN DESASTRE PARA DESCUBRIR QUE NO TENÍAS LA PROTECCIÓN
              ADECUADA
            </h2>

            <p className="text-xl text-gray-700" data-id="element-293">
              Cada día que pasas sin la cobertura correcta es un día en que tu
              familia está expuesta.
            </p>

            <p className="text-xl font-bold text-red-600" data-id="element-294">
              Un accidente. Una tormenta. Una factura médica inesperada.
              <br data-id="element-295" />
              Con eso es suficiente para cambiarlo todo.
            </p>

            <p className="text-lg text-gray-600 max-w-2xl mx-auto pt-4" data-id="element-296">
              La buena noticia: protegerse es más fácil de lo que crees. Una
              conversación con Angel Insurance es todo lo que necesitas para
              saber exactamente dónde estás parado — y construir un plan que
              proteja a tu familia de todo lo que la vida pueda traer.
            </p>
          </motion.div>

          {/* CTA Block */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-navy rounded-3xl p-8 md:p-12 shadow-2xl text-white text-center relative overflow-hidden" data-id="element-297">
            <div className="absolute top-0 left-0 w-full h-2 bg-gold" data-id="element-298"></div>

            <h3 className="text-2xl md:text-3xl font-bold text-gold mb-10" data-id="element-299">
              ESTO ES LO QUE DEBES HACER AHORA MISMO
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-300">
              <div className="flex flex-col items-center gap-3" data-id="element-301">
                <div className="bg-white/10 p-4 rounded-full" data-id="element-302">
                  <Phone size={32} className="text-gold" data-id="element-303" />
                </div>
                <h4 className="font-bold text-lg" data-id="element-304">Llámanos hoy</h4>
                <a href="tel:8134034867" className="text-xl font-bold hover:text-gold transition-colors" data-id="element-305">
                  (813) 403-4867
                </a>
              </div>

              <div className="flex flex-col items-center gap-3" data-id="element-306">
                <div className="bg-white/10 p-4 rounded-full" data-id="element-307">
                  <MapPin size={32} className="text-gold" data-id="element-308" />
                </div>
                <h4 className="font-bold text-lg" data-id="element-309">Visítanos</h4>
                <p className="text-center" data-id="element-310">
                  3840 W Humphrey St
                  <br data-id="element-311" />
                  Tampa, FL 33614
                </p>
              </div>

              <div className="flex flex-col items-center gap-3" data-id="element-312">
                <div className="bg-white/10 p-4 rounded-full" data-id="element-313">
                  <Clock size={32} className="text-gold" data-id="element-314" />
                </div>
                <h4 className="font-bold text-lg" data-id="element-315">Horario</h4>
                <p className="text-center" data-id="element-316">
                  Abierto de Lunes a Viernes
                  <br data-id="element-317" />
                  desde las 9 AM
                </p>
              </div>
            </div>

            {/* Storefront Photo & Interactive Map */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-id="element-318">
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.3,
              duration: 0.6
            }} className="rounded-2xl overflow-hidden border-2 border-gold/30 shadow-lg h-[280px] md:h-[320px]" data-id="element-319">
                <img src="https://cdn.magicpatterns.com/uploads/6TydWSvgdDjysbAvKdayDv/B1_angel_insurance.webp" alt="Oficina de Angel Insurance en Tampa" className="w-full h-full object-cover" data-id="element-320" />
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.4,
              duration: 0.6
            }} className="flex flex-col h-[280px] md:h-[320px]" data-id="element-321">
                <div className="rounded-2xl overflow-hidden border-2 border-gold/50 shadow-lg flex-grow" data-id="element-322">
                  <div ref={mapRef} className="w-full h-full z-0" data-id="element-323"></div>
                </div>
                <div className="mt-4" data-id="element-324">
                  <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 bg-gold/20 hover:bg-gold/40 text-gold font-bold text-sm px-6 py-3 rounded-full transition-colors" data-id="element-325">
                    <ExternalLink size={16} data-id="element-326" />
                    Abrir en Google Maps
                  </a>
                </div>
              </motion.div>
            </div>

            <p className="text-lg mb-8 text-gray-300" data-id="element-327">
              Entra a nuestra oficina. Llámanos. Comunícate con nosotros. Es
              gratis. Es fácil. Y podría ser la conversación más importante que
              tengas este año.
            </p>

            <a href="tel:8134034867" className="inline-block bg-gold hover:bg-yellow-500 text-navy font-bold text-xl px-10 py-5 rounded-full shadow-lg transition-transform hover:scale-105" data-id="element-328">
              La protección de tu familia comienza con una sola llamada
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}